import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { Section } from "@/components/section";
import { Reveal } from "@/components/reveal";
import { CtaBand } from "@/components/cta-band";
import { ResponsiveImg } from "@/components/responsive-img";
import { CLASSES } from "@/lib/site-data";
import { lcpPreload, imgSizes } from "@/lib/images";

export const Route = createFileRoute("/classes")({
  head: () => ({
    meta: [
      { title: "Art Classes | Magnolia House, Fuquay-Varina NC" },
      {
        name: "description",
        content:
          "Small-group pastel and oil painting classes for adults and young artists 13+ with Shirley Hunsberger in Fuquay-Varina, NC.",
      },
      {
        property: "og:title",
        content: "Art Classes with Shirley Hunsberger | Fuquay-Varina",
      },
      {
        property: "og:description",
        content: "Pastel and oil instruction in small groups — beginners welcome, ages 13 and up.",
      },
    ],
    links: [lcpPreload("class-students")],
  }),
  component: ClassesPage,
});

const CLASS_IMAGES = ["class-students", "class-palette", "class-stilllife"];

function ClassesPage() {
  return (
    <>
      <PageHero
        eyebrow="Art classes"
        title={
          <>
            Learn to see it
            <br />
            <span className="amber-text italic">before</span> you paint it.
          </>
        }
        lead="Small groups, real technique, honest critique — pastel and oil instruction for adults and serious young artists aged 13 and up."
        image="class-students"
        alt="Art students painting at Magnolia House"
      />

      <Section>
        <div className="grid gap-6 md:grid-cols-2">
          {CLASSES.map((c, i) => (
            <Reveal key={c.title} delay={(i % 2) * 90}>
              <article className="h-full rounded-xl bg-card p-8 shadow-[var(--shadow-soft)] transition-transform duration-700 hover:-translate-y-2">
                <p className="eyebrow">{String(i + 1).padStart(2, "0")}</p>
                <h2 className="display mt-4 text-3xl">{c.title}</h2>
                <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">{c.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="mist">
        <div className="grid gap-6 md:grid-cols-3">
          {CLASS_IMAGES.map((name, i) => (
            <Reveal key={name} delay={i * 90}>
              <div className="plate">
                <ResponsiveImg
                  name={name}
                  sizes={imgSizes.third}
                  alt="Art class at Magnolia House"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
